// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { CheckinStation } from '../../../types';
import { accountAPI } from '../../../utils/apiClient';
import logger from '../../../utils/logger';
import { Button } from '../../common/Button';

interface CheckinSummaryPickerProps {
  stations: CheckinStation[];
}

const MAX_SLOTS = 4;

export const CheckinSummaryPicker: React.FC<CheckinSummaryPickerProps> = ({ stations }) => {
  const [slots, setSlots] = useState<string[]>(['', '', '', '']);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<boolean>(false);

  const stationOptions = useMemo(
    () =>
      stations
        .filter((s) => !!s.deveui)
        .map((s) => ({ deveui: s.deveui as string, label: s.name || (s.deveui as string) })),
    [stations]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await accountAPI.getSummaryCheckpoints();
      const next = ['', '', '', ''];
      (res.checkpoints || []).forEach((c) => {
        if (c.position >= 0 && c.position < MAX_SLOTS) next[c.position] = c.deveui;
      });
      setSlots(next);
    } catch (err) {
      logger.error('Failed to load summary checkpoints', err);
      setError('Failed to load summary checkpoints');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (idx: number, value: string) => {
    setSaved(false);
    setSlots((prev) => prev.map((v, i) => (i === idx ? value : v)));
  };

  const duplicateDeveui = useMemo(() => {
    const picks = slots.filter(Boolean);
    return picks.length !== new Set(picks).size;
  }, [slots]);

  const handleSave = useCallback(async () => {
    if (duplicateDeveui) {
      setError('The same checkpoint cannot be selected twice');
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const deveuis = slots.filter(Boolean);
      const res = await accountAPI.setSummaryCheckpoints(deveuis);
      if (!res.success) {
        setError(res.error || 'Failed to save');
      } else {
        setSaved(true);
      }
    } catch (err) {
      logger.error('Failed to save summary checkpoints', err);
      setError('Failed to save summary checkpoints');
    } finally {
      setSaving(false);
    }
  }, [slots, duplicateDeveui]);

  if (loading) {
    return <div style={{ padding: '2vmin', fontSize: 'max(12px, 1.6vmin)' }}>Loading…</div>;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5vmin',
        padding: '2vmin',
        background: 'white',
        border: '1px solid #ddd',
        borderRadius: '1vmin',
      }}
    >
      <div style={{ fontSize: 'max(13px, 1.8vmin)', fontWeight: 600 }}>Top-of-screen summary</div>
      <div style={{ fontSize: 'max(11px, 1.4vmin)', color: '#666' }}>
        Pick up to {MAX_SLOTS} checkpoints. Their live checked-in count appears in the summary nodes
        at the top of the main view.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8vmin' }}>
        {slots.map((value, idx) => (
          <label
            key={idx}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1vmin',
              fontSize: 'max(12px, 1.5vmin)',
            }}
          >
            <span style={{ width: 'max(48px, 6vmin)' }}>Slot {idx + 1}</span>
            <select
              value={value}
              onChange={(e) => handleChange(idx, e.target.value)}
              style={{
                flex: 1,
                padding: 'max(4px, 0.6vmin) max(8px, 1vmin)',
                fontSize: 'max(12px, 1.5vmin)',
              }}
            >
              <option value="">— none —</option>
              {stationOptions.map((o) => (
                <option key={o.deveui} value={o.deveui}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
      {duplicateDeveui && (
        <div style={{ color: '#c00', fontSize: 'max(11px, 1.4vmin)' }}>
          Each slot must reference a different checkpoint.
        </div>
      )}
      {error && <div style={{ color: '#c00', fontSize: 'max(11px, 1.4vmin)' }}>{error}</div>}
      {saved && !error && (
        <div style={{ color: '#2a7', fontSize: 'max(11px, 1.4vmin)' }}>Saved.</div>
      )}
      <div>
        <Button
          text={saving ? 'Saving…' : 'Save'}
          onClick={handleSave}
          disabled={saving || duplicateDeveui}
        />
      </div>
    </div>
  );
};
